/**
 * Tenant-scoped storage helpers for the auth store.
 *
 * All auth keys are prefixed with the tenant code so that two tenants opened in the same browser
 * never read each other's state. Everything lives in sessionStorage: the lifetime of an auth
 * decision is the lifetime of the tab, and a tenant-scoped (rather than user-scoped) flag kept in
 * localStorage would let one user's failure permanently affect every other user of that browser
 * profile - which is exactly the HRI2-17930 defect.
 *
 * Every accessor is guarded. Storage throws in partitioned or blocked-storage contexts, and these
 * helpers are called from the invalid-session teardown and from `catch` blocks, where a second
 * throw would escape as an unhandled rejection. A read that cannot be answered reports "not set"
 * so a storage failure can never wedge a tenant out of logging in.
 */

type TenantId = string | null | undefined;

const authStoreKey = (tenantId: string, name: string) => `${tenantId}_authStore_${name}`;

/**
 * Keys removed when a tenant session is torn down. Mirrors what logout() clears.
 */
const tenantSessionKeys = ['AccessToken', 'RefreshToken', 'LoginAttempted', 'Initialized'];

const disableAutoLoginName = 'DisableAutoLogin';
const forceReauthName = 'ForceReauth';

function readSessionValue(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    console.warn('Could not read the auth session storage:', error);
    return null;
  }
}

function writeSessionValue(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    console.warn('Could not write to the auth session storage:', error);
  }
}

function removeSessionValue(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn('Could not clear the auth session storage:', error);
  }
}

/**
 * Whether the automatic redirect to the identity provider should be suppressed for this tenant in
 * the current browsing session. Without a tenant there is no key to read, and initialize() only
 * ever asks about a real tenant, so an unidentified caller is never treated as disabled.
 */
export function isAutoLoginDisabled(tenantId: TenantId): boolean {
  if (!tenantId) {
    return false;
  }
  return readSessionValue(authStoreKey(tenantId, disableAutoLoginName)) === 'true';
}

/**
 * Suppresses the automatic redirect to the identity provider for the rest of this browsing session.
 * Used after an explicit logout, a failed code exchange, or an invalid-session teardown, so the
 * user lands on the login form instead of being bounced straight back to a live IdP session.
 *
 * Without a tenant this is a no-op rather than a write under a placeholder key: initialize() reads
 * the flag back by real tenant code, so such a key could never be read again.
 */
export function disableAutoLogin(tenantId: TenantId): void {
  if (!tenantId) {
    return;
  }
  writeSessionValue(authStoreKey(tenantId, disableAutoLoginName), 'true');
}

/**
 * Re-allows the automatic redirect to the identity provider, after a successful sign-in.
 */
export function enableAutoLogin(tenantId: TenantId): void {
  if (!tenantId) {
    return;
  }
  removeSessionValue(authStoreKey(tenantId, disableAutoLoginName));
}

/**
 * Whether the next sign-in must force re-authentication at the upstream identity provider
 * (`prompt=login`). Cognito's /logout ends only its own hosted-UI session, so without this the IdP
 * silently re-asserts the same identity and the user can never reach a different account.
 *
 * Session-scoped on purpose: it has to survive the full-page navigation to Cognito and back in
 * this tab, without leaking into other tabs signed in as somebody else.
 */
export function isForceReauthRequired(tenantId: TenantId): boolean {
  if (!tenantId) {
    return false;
  }
  return readSessionValue(authStoreKey(tenantId, forceReauthName)) === 'true';
}

/**
 * Requires the next sign-in to re-authenticate at the identity provider.
 */
export function requireForceReauth(tenantId: TenantId): void {
  if (!tenantId) {
    return;
  }
  writeSessionValue(authStoreKey(tenantId, forceReauthName), 'true');
}

/**
 * Consumes the force-re-authentication request. Called once the redirect is actually happening, so
 * that prompting applies to a single sign-in rather than defeating auto-login from then on.
 */
export function clearForceReauth(tenantId: TenantId): void {
  if (!tenantId) {
    return;
  }
  removeSessionValue(authStoreKey(tenantId, forceReauthName));
}

/**
 * Removes the tenant's stored tokens and session markers.
 */
export function clearTenantAuthSession(tenantId: TenantId): void {
  if (!tenantId) {
    return;
  }
  for (const name of tenantSessionKeys) {
    removeSessionValue(authStoreKey(tenantId, name));
  }
}

/**
 * Removes the pre-HRI2-17930 auto-login flag, which was written to localStorage and therefore never
 * expired. Called on every initialize() so already-poisoned browser profiles self-heal on their
 * next visit; without it those profiles would stay broken after this change ships.
 */
export function purgeLegacyAutoLoginFlag(tenantId: TenantId): void {
  if (!tenantId) {
    return;
  }
  try {
    localStorage.removeItem(authStoreKey(tenantId, disableAutoLoginName));
  } catch (error) {
    // localStorage can throw in restricted browser modes; the purge is best-effort.
    console.warn('Could not purge the legacy auto-login flag:', error);
  }
}
